import type { StrictLocaleObject } from '@layers/i18n/shared/locales'
import type { NuxtOptions } from '@nuxt/schema'
import { cs, de, dk, en, es, eu, fr, it, nl, pl, sk } from '@layers/i18n/shared/locales'

export type { LocaleObject } from '@nuxtjs/i18n'

// The locales this storefront actually ships — one per Vendure channel. Exported
// so UI (e.g. the header currency picker) can list exactly these, rather than
// useI18n().locales which is merged with the i18n layer's full locale set.
//
// hu and ro are intentionally excluded: those channels have no products, so
// serving /hu/ and /ro/ published an empty, indexable storefront and their
// product URLs 404'd out of the sitemap (see server/plugins/sitemap-data-feed-chunks.ts).
// Re-add them once those markets actually launch.
//
// `eu` is the English euro-area storefront backed by `en-eu-channel`, and is a
// market rather than a language: it carries the same English catalogue as `en`
// but prices it in EUR and quotes the channel's European couriers.
//
// `it`, `es` and `fr` are the single-country euro markets behind `it-channel`,
// `es-channel` and `fr-channel` — EUR like `eu`, but each quoting only its own
// country's couriers and extracting that country's VAT rate. Unlike `eu` they
// are real languages: the shared i18n layer already carries `it.json`/`es.json`/
// `fr.json`, so the chrome is translated and each locale keeps its own `file`.
//
// `dk` and `nl` follow the same pattern behind `dk-channel` and `nl-channel`.
// Denmark's locale is coded by COUNTRY (`dk`), so the URL is /dk/ and the
// channel token, feed and Stripe keys all read `dk`; only `language` stays
// `da-DK`, because that is what Vendure is asked for and it has no `dk`.
// Both price in EUR — Denmark included, so that its catalogue and courier
// tariffs could be cloned from the other euro markets rather than converted.
export const appLocales: StrictLocaleObject[] = [en, eu, cs, sk, pl, de, it, es, fr, dk, nl]

// Every message comes from the i18n layer (layers/i18n/locales/<file>), which
// registers these same locale objects with their `file`; @nuxtjs/i18n merges a
// locale's files across layers by code. Leave `file` off here: the module
// resolves a relative `file` against this app's own langDir
// (apps/exalted.com/i18n/locales/), which no longer exists, so the build would
// fail. The app kept its own locale files there (plus fabric-guide ones) from
// when the layer was shared with other storefronts; they are now part of the
// layer's. Without a `langDir` of its own the app may list locales without
// files — the module only demands them when `langDir` is set.
type AppLocale = Omit<StrictLocaleObject, 'file'>

function withoutFile(l: StrictLocaleObject): AppLocale {
  const { file, ...rest } = l
  return rest
}

export const i18n: Omit<NuxtOptions['i18n'], 'locales'> & { locales: AppLocale[] } = {
  locales: appLocales.map(withoutFile),
  lazy: true,
  defaultLocale: cs.code,

  strategy: 'prefix',
  trailingSlash: false,
  detectBrowserLanguage: {
    useCookie: true,
    fallbackLocale: cs.code,
    cookieKey: 'locale',
    redirectOn: 'root',
  },
}
